import { Box, Container } from '@mui/material';
import { captureMessage } from '@sentry/nextjs';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { useLocale } from 'hooks/useLocale';
import { useAccount } from 'queries/accounts/useAccount';
import { usePageEvents } from 'queries/events/usePageEvents';
import { useTransferByShareCode } from 'queries/tickets/useTransferByShareCode';

import { isEventTicketingEnabled, isPastEvent } from 'components/@events/utils';
import AppCompactNavigation from 'components/@navigation/AppCompactNavigation';
import Page404 from 'components/@screens/Page404';
import InvalidTransfer from 'components/@transfer/InvalidTransfer';
import TransferAcceptedScreen from 'components/@transfer/TransferAcceptedScreen';
import TransferDetails from 'components/@transfer/TransferDetails';

import Footer from './Footer';
import { useQueryShareCode } from './useQueryShareCode';

const EventTransferContainer = () => {
  const { isReady } = useRouter();
  const { locale } = useLocale();
  const shareCode = useQueryShareCode();
  const {
    isPending: isPendingTransfer,
    isError: isTransferError,
    transfer,
  } = useTransferByShareCode({ shareCode });
  const { events, isPending: isPendingEvent } = usePageEvents(
    {
      first: 500,
      locale,
      filter: {
        ticketsEventId: { eq: transfer?.eventId },
        OR: [
          { ticketsTimeslotId: { isBlank: true } },
          { ticketsTimeslotId: { in: transfer?.timeslotIds } },
        ],
      },
      orderBy: 'startDatetime_ASC',
    },
    { enabled: !!transfer },
  );
  const { isLoading: isLoadingUser } = useAccount();
  const [transferAccepted, setTransferAccepted] = useState(false);

  const handleAcceptTransfer = () => setTransferAccepted(true);

  // edge case: when a transfer contains multiple events, we hide event information
  // https://mdlbeasts.atlassian.net/wiki/spaces/MN/pages/861700101/Matching+ticketing+events+x+CMS+events
  const hasMultipleEvents = (events?.length ?? 0) > 1;

  useEffect(() => {
    if (!events?.length || !transfer) {
      return;
    }
    // Log to sentry when we find multiple datoCMS events. This is a possible edge case.
    // Automate logs so we can let ticketing know when their data is not correct.
    if (hasMultipleEvents) {
      captureMessage(
        `[TRANSFER - ACCEPT] Multiple events found for event ${transfer.eventId}`,
        {
          level: 'warning',
          tags: {
            transferId: transfer?.id,
            timeslotIds: transfer?.timeslotIds?.toString(),
            datoCmsEventIds: events?.map((e) => e.id).toString(),
          },
        },
      );
    }
  }, [hasMultipleEvents, events, transfer]);

  const event = hasMultipleEvents
    ? events?.filter(
        (e) =>
          e.ticketsTimeslotId &&
          transfer?.timeslotIds?.includes(e.ticketsTimeslotId) &&
          !isPastEvent(e),
      )?.[0]
    : events?.[0];

  const eventInvalid =
    !!event && (isPastEvent(event) || !isEventTicketingEnabled(event));

  if (transferAccepted && transfer && event) {
    return (
      <TransferAcceptedScreen
        event={event}
        hasMultipleEvents={hasMultipleEvents}
      />
    );
  }

  const checkLoadingTransfer = () => {
    if (!isReady) return true;

    if (!shareCode && isReady) {
      return false;
    }

    if (isPendingTransfer || isLoadingUser) {
      return true;
    }

    if (isPendingEvent && !isTransferError) {
      return true;
    }

    return false;
  };

  const isLoadingTransfer = checkLoadingTransfer();

  const isInvalidTransfer =
    (!!shareCode && (isTransferError || transfer?.status !== 'Pending')) ||
    eventInvalid;

  return (
    <AppCompactNavigation isLoading={isLoadingTransfer}>
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}
      >
        <Box
          sx={{
            display: { xs: 'flex', md: 'grid' },
            gridTemplateColumns: '6fr 1fr 5fr',
            flexDirection: { xs: 'column', md: 'row' },
            flex: '1',
          }}
        >
          {isInvalidTransfer && event ? (
            <InvalidTransfer transfer={transfer} eventInvalid={eventInvalid} />
          ) : transfer && event ? (
            <TransferDetails
              transfer={transfer}
              event={event}
              hasMultipleEvents={hasMultipleEvents}
            />
          ) : (
            <Page404 />
          )}
          <Box sx={{ display: { xs: 'none', md: 'block' } }} />
          {!isInvalidTransfer && !isLoadingTransfer && transfer && !!event && (
            <Footer
              transferId={transfer.id}
              transferVersion={transfer.version}
              onAcceptTransfer={handleAcceptTransfer}
            />
          )}
        </Box>
      </Container>
    </AppCompactNavigation>
  );
};

export default EventTransferContainer;
